import React, { useState, useRef, useEffect } from 'react';
import Icon from '@salesforce/design-system-react/components/icon';
import { colors, type, space, radius, elevation, scoreColor } from '../tokens';

const DIMENSIONS = { low: 'Low', mid: 'Medium', high: 'High' };

const DonorScoreBadge = ({ score = 0, breakdown, size = 'md' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const color = scoreColor(score);
  const tier = score >= 70 ? DIMENSIONS.high : score >= 40 ? DIMENSIONS.mid : DIMENSIONS.low;
  const padY = size === 'sm' ? 3 : 5;
  const padX = size === 'sm' ? 10 : 14;
  const fontSize = size === 'sm' ? 13 : 15;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => breakdown && setOpen(!open)}
        aria-haspopup={Boolean(breakdown)}
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: space[2],
          background: `${color}14`,
          border: `1.5px solid ${color}`,
          color,
          padding: `${padY}px ${padX}px`,
          borderRadius: radius.pill,
          fontSize,
          fontFamily: 'inherit',
          fontWeight: 700,
          cursor: breakdown ? 'pointer' : 'default',
          lineHeight: 1.2,
        }}
      >
        <span style={{ ...type.label, color, letterSpacing: 0.4 }}>Score</span>
        <span style={{ fontSize: fontSize + 2, fontWeight: 800 }}>{score}</span>
        <span style={{ ...type.micro, color, opacity: 0.7 }}>/ 100</span>
        {breakdown && (
          <Icon
            category="utility"
            name={open ? 'chevronup' : 'chevrondown'}
            size="xx-small"
            style={{ fill: color, marginLeft: -2 }}
          />
        )}
      </button>

      {open && breakdown && (
        <div
          role="dialog"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            boxShadow: elevation.cardHover,
            padding: space[4],
            minWidth: 260,
            zIndex: 20,
          }}
        >
          <div style={{ ...type.label, color: colors.textTertiary, marginBottom: space[2] }}>
            Score breakdown — {tier}
          </div>
          {Object.entries(breakdown).map(([dim, val]) => (
            <div key={dim} style={{ marginBottom: space[2] }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 4,
                ...type.small,
                color: colors.textPrimary,
              }}>
                <span style={{ textTransform: 'capitalize' }}>{dim}</span>
                <span style={{ fontWeight: 600, color: scoreColor(val) }}>{val}</span>
              </div>
              <div style={{ height: 4, background: colors.surfaceInk, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  width: `${val}%`,
                  height: '100%',
                  background: scoreColor(val),
                  transition: 'width 240ms',
                }} />
              </div>
            </div>
          ))}
          <p style={{ ...type.micro, color: colors.textTertiary, marginTop: space[3], marginBottom: 0, lineHeight: 1.5 }}>
            Composite score derived from wealth indicators, engagement signals, and
            individual / household giving behavior.
          </p>
        </div>
      )}
    </div>
  );
};

export default DonorScoreBadge;
